import * as Localization from 'expo-localization';
import Analytics from '@/utils/analytics';
import countries from '@assets/countries.json';
import i18n from '@/utils/i18n';
import React, { useMemo, useState } from 'react';
import { CountryCode } from 'libphonenumber-js';
import { ListOption } from './list-item';
import { Selector } from './selector';
import { StyleProp, StyleSheet, Text, ViewStyle } from 'react-native';
import { theme, TYPOGRAPHY } from '@/styles';

export interface Country {
  code: string;
  dialCode: string;
  flag: string;
  name: string;
}

export type CountryListOption = ListOption<Country>;

const getCountrySections = (
  searchText: string,
  initialCountryCode?: string,
) => {
  const selectedCountryCode =
    initialCountryCode || Localization.getLocales()[0].regionCode;
  const countryMap = countries.reduce<{ [key: string]: CountryListOption[] }>(
    (result, country) => {
      // https://stackoverflow.com/a/37511463
      const key = country.name
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')[0];
      if (!result[key]) {
        result[key] = [];
      }

      // Skip the selected country when there is no search text since it'll be displayed first
      // Skip if the country does not match search text
      const includeCountry = searchText
        ? country.name.toLowerCase().includes(searchText.toLowerCase()) ||
          country.code.toLowerCase().includes(searchText.toLowerCase()) ||
          country.dialCode.includes(searchText)
        : country.code !== selectedCountryCode;

      if (includeCountry) {
        result[key].push({
          metadata: country,
          title: country.name,
          value: country.code,
        });
      }

      return result;
    },
    {},
  );

  const sections = Object.keys(countryMap)
    .sort()
    .map((key) => ({
      data: countryMap[key],
      key,
    }));

  // Include selected country first if there is no search text
  if (!searchText) {
    const selectedCountry = countries.find(
      (c) => c.code === selectedCountryCode,
    ) as Country;
    sections.unshift({
      data: [
        {
          metadata: selectedCountry,
          title: selectedCountry.name,
          value: selectedCountry.code,
        },
      ],
      key: '',
    });
  }

  return sections;
};

interface CountrySelectorProps {
  /** Initial country code to show selected */
  initialCountryCode?: CountryCode;
  /** Handler for when a country is selected */
  onCountrySelect: (countryCode: CountryCode) => void;
  /** Style for the selector */
  style?: StyleProp<ViewStyle>;
}

export const CountrySelector: React.FC<CountrySelectorProps> = ({
  initialCountryCode,
  onCountrySelect,
  style,
}) => {
  const [searchText, setSearchText] = useState('');

  const countrySections = useMemo(
    () => getCountrySections(searchText, initialCountryCode),
    [initialCountryCode, searchText],
  );

  return (
    <Selector<CountryListOption>
      estimatedItemSize={56}
      label={i18n.t('select_a_country')}
      listItemProps={{
        renderLeftComponent: ({ metadata }: { metadata?: Country }) => (
          <Text style={styles.listItemFlag}>{metadata?.flag}</Text>
        ),
        renderRightComponent: ({ metadata }: { metadata?: Country }) => (
          <Text style={TYPOGRAPHY.listSmall}>{metadata?.dialCode}</Text>
        ),
      }}
      onSearchInputChange={setSearchText}
      onSelect={(option) => {
        const countryCode = option.metadata!.code;
        Analytics.track('country_selector.country.select', { countryCode });
        onCountrySelect(countryCode as CountryCode);
      }}
      placeholder={i18n.t('select_a_country')}
      sections={countrySections}
      style={style}
    />
  );
};

const styles = StyleSheet.create({
  listItemFlag: {
    fontSize: theme.fontSizes.h2,
    minWidth: 40,
  },
});
